import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import EntryFormHeader from '../../components/header/EntryFormHeader';
import EntryFormInput from '../../components/legalPersonEntryForm/Input';
import SelectPrefecture from '../../components/legalPersonEntryForm/SelectPrefecture';
import PrivacyPolicyModal from '../../components/modal/PrivacyPolicyModal';
import TermsModal from '../../components/modal/TermsModal';
import { setField, setMultipleFields } from '../../redux/entryFormSlice';
import { RootState } from '../../redux/store';

interface LegalPersonalEntryFormType {
}

const LegalPersonalEntryForm: React.FC<LegalPersonalEntryFormType> = () => {
  const entryFormInfo = useSelector((state: RootState) => state.entryForm);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState<string>(entryFormInfo.companyName);
  const [companyNameErr, setCompanyNameErr] = useState<string>('');
  const [corporateNumber, setCorporateNumber] = useState<string>(entryFormInfo.corporateNumber);
  const [corporateNumberErr, setCorporateNumberErr] = useState<string>('');
  const [companyPostalCode, setCompanyPostalCode] = useState<string>(entryFormInfo.companyPostalCode);
  const [companyPostalCodeErr, setCompanyPostalCodeErr] = useState<string>('');
  const [isLoadingCompanyPostalCodeValid, setIsLoadingCompanyPostalCodeValid] = useState<boolean>(false);
  const [companyPrefecture, setCompanyPrefecture] = useState<string>(entryFormInfo.companyPrefecture);
  const [companyPrefectureErr, setCompanyPrefectureErr] = useState<string>('');
  const [companyCity, setCompanyCity] = useState<string>(entryFormInfo.companyCity);
  const [companyStreetAddress, setCompanyStreetAddress] = useState<string>(entryFormInfo.companyStreetAddress);
  const [companyBuilding, setCompanyBuilding] = useState<string>(entryFormInfo.companyBuilding);
  const [representativeFirstName, setRepresentativeFirstName] = useState<string>(entryFormInfo.representativeFirstName);
  const [representativeFirstNameErr, setRepresentativeFirstNameErr] = useState<string>('');
  const [representativeLastName, setRepresentativeLastName] = useState<string>(entryFormInfo.representativeLastName);
  const [representativeLastNameErr, setRepresentativeLastNameErr] = useState<string>('');
  const [firstName, setFirstName] = useState<string>(entryFormInfo.firstName);
  const [firstNameErr, setFirstNameErr] = useState<string>('');
  const [lastName, setLastName] = useState<string>(entryFormInfo.lastName);
  const [lastNameErr, setLastNameErr] = useState<string>('');
  const [firstNameKa, setFirstNameKa] = useState<string>(entryFormInfo.firstNameKa);
  const [firstNameKaErr, setFirstNameKaErr] = useState<string>('');
  const [lastNameKa, setLastNameKa] = useState<string>(entryFormInfo.lastNameKa);
  const [lastNameKaErr, setLastNameKaErr] = useState<string>('');
  const [birthday, setBirthday] = useState<string>(entryFormInfo.birthday);
  const [birthdayErr, setBirthdayErr] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>(entryFormInfo.postalCode);
  const [postalCodeErr, setPostalCodeErr] = useState<string>('');
  const [isLoadingPostalCodeValid, setIsLoadingPostalCodeValid] = useState<boolean>(false);
  const [prefecture, setPrefecture] = useState<string>(entryFormInfo.prefecture);
  const [prefectureErr, setPrefectureErr] = useState<string>('');
  const [city, setCity] = useState<string>(entryFormInfo.city);
  const [address, setAddress] = useState<string>(entryFormInfo.address);
  const [building, setBuilding] = useState<string>(entryFormInfo.building);
  const [gender, setGender] = useState<string>(entryFormInfo.gender);
  const [genderErr, setGenderErr] = useState<string>('');
  const [phoneNum, setPhoneNum] = useState<string>(entryFormInfo.phoneNum);
  const [phoneNumErr, setPhoneNumErr] = useState<string>('');
  const [email, setEmail] = useState<string>(entryFormInfo.email);
  const [emailErr, setEmailErr] = useState<string>('');
  const [isAgreeTerm, setIsAgreeTerm] = useState<boolean>(false);
  const [isAgreeTermErr, setIsAgreeTermErr] = useState<string>('');
  const [isAgreePrivacyPolicy, setIsAgreePrivacyPolicy] = useState<boolean>(false);
  const [isAgreePrivacyPolicyErr, setIsAgreePrivacyPolicyErr] = useState<string>('');
  const [isAvailableToNextStep, setIsAvailableToNextStep] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<string>(entryFormInfo.selectedRole);
  const [selectedRoleErr, setSelectedRoleErr] = useState<string>('');
  const [isPrivacyPolicyModalOpen, setIsPrivacyPolicyModalOpen] = useState<boolean>(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState<boolean>(false);

  const handleValidationToNextStep = () => {
    const requiredValidations = {
      companyName: handleRequiredValidation(companyName),
      corporateNumber: handleRequiredValidation(corporateNumber),
      companyPostalCode: handleRequiredValidation(companyPostalCode),
      companyPrefecture: handleRequiredValidation(companyPrefecture),
      representativeLastName: handleRequiredValidation(representativeLastName),
      representativeFirstName: handleRequiredValidation(representativeFirstName),
      lastName: handleRequiredValidation(lastName),
      firstName: handleRequiredValidation(firstName),
      lastNameKa: handleRequiredValidation(lastNameKa),
      firstNameKa: handleRequiredValidation(firstNameKa),
      birthday: handleRequiredValidation(birthday),
      postalCode: handleRequiredValidation(postalCode),
      prefecture: handleRequiredValidation(prefecture),
      gender: handleRequiredValidation(gender),
      phoneNum: handleRequiredValidation(phoneNum),
      email: handleRequiredValidation(email),
      role: handleRequiredValidation(selectedRole)
    };

    setCompanyNameErr(requiredValidations.companyName);
    setCompanyPostalCodeErr(requiredValidations.companyPostalCode);
    setCompanyPrefectureErr(requiredValidations.companyPrefecture);
    setRepresentativeLastNameErr(requiredValidations.representativeLastName);
    setRepresentativeFirstNameErr(requiredValidations.representativeFirstName);
    setLastNameErr(requiredValidations.lastName);
    setFirstNameErr(requiredValidations.firstName);
    setLastNameKaErr(requiredValidations.lastNameKa);
    setFirstNameKaErr(requiredValidations.firstNameKa);
    setBirthdayErr(requiredValidations.birthday);
    setPostalCodeErr(requiredValidations.postalCode);
    setPrefectureErr(requiredValidations.prefecture);
    setGenderErr(requiredValidations.gender);
    setPhoneNumErr(requiredValidations.phoneNum);
    setEmailErr(requiredValidations.email);
    setSelectedRoleErr(requiredValidations.role);

    if (!(isValidCorporateNumber(corporateNumber))) {
      setCorporateNumberErr('有効な法人番号ではありません');
    }

    if (!requiredValidations.email) {
      setEmailErr(handleRegexValidation(
        email,
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        '正しいメールアドレスの形式で入力してください'
      ));
    }

    if (!requiredValidations.phoneNum) {
      setPhoneNumErr(handleRegexValidation(
        phoneNum,
        /^0\d{9,10}$/,
        '正しい電話番号の形式で入力してください'
      ));
    }

    if (!requiredValidations.firstNameKa) {
      setFirstNameKaErr(handleRegexValidation(
        firstNameKa,
        /^[ァ-ヶー　]+$/,
        'カタカナで入力してください'
      ));
    }

    if (!requiredValidations.lastNameKa) {
      setLastNameKaErr(handleRegexValidation(
        lastNameKa,
        /^[ァ-ヶー　]+$/,
        'カタカナで入力してください'
      ));
    }

    if(!isAgreeTerm)
      setIsAgreeTermErr('利用規約への同意が必要です');

    if(!isAgreePrivacyPolicy)
      setIsAgreePrivacyPolicyErr('プライバシーポリシーへの同意が必要です');

    if(!selectedRole)
      setSelectedRoleErr('役割を選択');
    
    if(
      isAvailableToNextStep &&
      companyName &&
      corporateNumber &&
      companyPostalCode &&
      companyPrefecture &&
      representativeLastName &&
      representativeFirstName &&
      lastName &&
      firstName &&
      lastNameKa &&
      firstNameKa &&
      birthday &&
      postalCode &&
      prefecture &&
      gender &&
      phoneNum &&
      email &&
      selectedRole &&
      isAgreeTerm &&
      isAgreePrivacyPolicy
    ) {
      dispatch(setMultipleFields({
        companyName,
        corporateNumber,
        companyPostalCode,
        companyPrefecture,
        companyCity,
        companyStreetAddress,
        companyBuilding,
        representativeLastName,
        representativeFirstName,
        lastName,
        firstName,
        lastNameKa,
        firstNameKa,
        birthday,
        postalCode,
        prefecture,
        city,
        address,
        building,
        gender,
        phoneNum,
        email,
        selectedRole
      }));

      dispatch(setField({field: 'entryFormStep', value: 'confirm'}))
      
      navigate('/form/legalPersonal/confirm');
    }
  }

  useEffect(() => {
    setIsAvailableToNextStep(
      !companyNameErr &&
      !corporateNumberErr &&
      !companyPostalCodeErr &&
      !companyPrefectureErr &&
      !representativeLastNameErr &&
      !representativeFirstNameErr &&
      !lastNameErr &&
      !firstNameErr &&
      !lastNameKaErr &&
      !firstNameKaErr &&
      !birthdayErr &&
      !postalCodeErr &&
      !prefectureErr &&
      !genderErr &&
      !phoneNumErr &&
      !emailErr &&
      isAgreeTerm &&
      isAgreePrivacyPolicy
    );
  }, [
    companyNameErr,
    corporateNumberErr,
    companyPostalCodeErr,
    companyPrefectureErr,
    representativeLastNameErr,
    representativeFirstNameErr,
    lastNameErr,
    firstNameErr,
    lastNameKaErr,
    firstNameKaErr,
    birthdayErr,
    postalCodeErr,
    prefectureErr,
    genderErr,
    phoneNumErr,
    emailErr,
    isAgreeTerm,
    isAgreePrivacyPolicy
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePostalCodeValidation = useCallback(async (postcd: string, type: string): Promise<boolean> => {
    const postcdNum = postcd.replace('-', '');
    if(postcdNum.length !== 7) {
      if(type === 'postalCode') {
        setPostalCodeErr("正しい郵便番号の形式で入力してください");
      } else {
        setCompanyPostalCodeErr("正しい郵便番号の形式で入力してください");
      }
    }

    if(type === 'postalCode')
      setIsLoadingPostalCodeValid(true);
    else
      setIsLoadingCompanyPostalCodeValid(true);

    try {
      const response = await fetch(`https://api.zipaddress.net/?zipcode=${postcd}`);
      const data = await response.json();
      if(data.code === 200) {      
        if(type === 'postalCode') {
          setPostalCodeErr("");
          setCity(data.data.city);
          setAddress(data.data.town);
          setPrefecture(data.data.pref);
        } else {
          setCompanyPostalCodeErr("");
          setCompanyCity(data.data.city);
          setCompanyStreetAddress(data.data.town);
          setCompanyPrefecture(data.data.pref);
        }

        return true;
      }
      else {
        if(type === 'postalCode') {
          setPostalCodeErr('郵便番号が見つかりませんでした');
        } else {
          setCompanyPostalCodeErr('郵便番号が見つかりませんでした');
        }

        return false;
      }
    } catch (err) {
      if(type === 'postalCode') {
        setPostalCodeErr('郵便番号の検索中にエラーが発生しました');
      } else {
        setCompanyPostalCodeErr('郵便番号の検索中にエラーが発生しました');
      }

      return false;
    } finally {
      if(type === 'postalCode')
        setIsLoadingPostalCodeValid(false);
      else
        setIsLoadingCompanyPostalCodeValid(false);
    }
  }, [])

  const handleRequiredValidation = useCallback((value: string) => {
    if(value === '') {
      return '必須項目です';
    } else {
      return '';
    }
  }, [])

  const handleRegexValidation = useCallback((value: string, regex: RegExp, errorMsg: string): string => {
    if(value === null)
      return '';
    if(regex.test(value))
      return '';
    return errorMsg;
  }, [])

  const isValidCorporateNumber = useCallback((inputCorporateNumber: string): boolean => {
    // 1. Basic format check
    if (!inputCorporateNumber) return false;
    
    // Remove non-numeric characters (considering possible hyphens)
    const numbers = inputCorporateNumber.replace(/[^0-9]/g, '');
    
    // 2. Check length (must be 13 digits)
    if (numbers.length !== 13) return false;
    
    // 3. First digit must be 1-9
    if (numbers[0] === '0') return false;

    // 4. Validate check digit (13th digit)
    try {
        const digits = numbers.split('').map(Number);
        const checkDigit = digits[12]; // 13th digit is the check digit
        
        // Calculate check digit
        // For digits 1-12:
        // Odd positions (1,3,5,7,9,11): multiply by 1
        // Even positions (2,4,6,8,10,12): multiply by 2
        let sum = 0;
        for (let i = 0; i < 12; i++) {
            const multiplier = (i % 2 === 0) ? 1 : 2;
            sum += digits[i] * multiplier;
        }
        
        // Subtract remainder of sum divided by 9 from 9 (if 9, keep 9)
        const calculatedCheckDigit = 9 - (sum % 9);
        const expectedCheckDigit = (calculatedCheckDigit === 9) ? 9 : calculatedCheckDigit;
        
        // Compare calculated check digit with actual value
        return checkDigit === expectedCheckDigit;
        
    } catch (error) {
        return false;
    }
  }, [])

  return (
    <>
      <PrivacyPolicyModal
        isOpen={isPrivacyPolicyModalOpen}
        onClose={() => setIsPrivacyPolicyModalOpen(false)}
        onConfirm={() => {
          setIsAgreePrivacyPolicy(true);
          setIsAgreePrivacyPolicyErr('');
        }}
      />
      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        onConfirm={() => {
          setIsAgreeTerm(true);
          setIsAgreeTermErr('');
        }}
      />
      <div className="bg-gray-50">
        <EntryFormHeader currentStep={1} />
        <main className="form-container">
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-8">会社情報入力</h2>
            <form id="companyInfoForm" className="space-y-6">
              <div className="form-group">
                <label className="form-label required">会社名</label>
                <EntryFormInput
                  type='text'
                  placeholder='例：株式会社○○○○'
                  value={companyName}
                  errorMessage={companyNameErr}
                  onChange={(value: string) => {
                    setCompanyName(value);
                    setCompanyNameErr(handleRequiredValidation(value));
                  }}
                />
              </div>
              <div className="form-group">
                <label className="form-label required">法人番号</label>
                <EntryFormInput
                  type='text'
                  placeholder='13桁の数字'
                  value={corporateNumber}
                  errorMessage={corporateNumberErr}
                  onChange={(value: string) => {
                    setCorporateNumber(value);

                    if(isValidCorporateNumber(value)) {
                      setCorporateNumberErr('');
                    } else {
                      setCorporateNumberErr('有効な法人番号ではありません');
                    }
                  }}
                />
              </div>

              <div className="form-group">
                <label className="form-label required">会社所在地</label>
                <div className="space-y-3">
                  <EntryFormInput
                    type='text'
                    placeholder='郵便番号'
                    value={companyPostalCode}
                    errorMessage={companyPostalCodeErr}
                    onChange={(value: string) => {
                      const numericValue = value.replace(/\D/g, '');

                      let formattedValue = numericValue;

                      if (numericValue.length > 3) {
                        formattedValue = numericValue.slice(0, 3) + '-' + numericValue.slice(3, 7);
                      }

                      setCompanyPostalCode(formattedValue);
                      const requiredValidation = handleRequiredValidation(formattedValue);

                      if (requiredValidation) {
                        setCompanyPostalCodeErr(requiredValidation);
                      } else {
                        setCompanyPostalCodeErr(
                          handleRegexValidation(
                            numericValue,
                            /^\d{7}$/,
                            '正しい郵便番号の形式で入力してください'
                          )
                        );
                      }

                      if(numericValue.length === 7) {
                        handlePostalCodeValidation(numericValue, 'companyPostalCode');
                      }
                    }}
                  />
                  {isLoadingCompanyPostalCodeValid && <div className="loading">検索中...</div>}
                  <SelectPrefecture
                    value={companyPrefecture ?? ''}
                    onChange={(value: string) => {
                      setCompanyPrefecture(value);
                      setCompanyPrefectureErr(handleRequiredValidation(value));
                    }}
                    errorMessage={companyPrefectureErr}
                  />
                  <EntryFormInput
                    type='text'
                    placeholder='市区町村'
                    value={companyCity}
                    onChange={(value: string) => {
                      setCompanyCity(value);
                    }}
                  />
                  <EntryFormInput
                    type='text'
                    placeholder='番地'
                    value={companyStreetAddress}
                    onChange={(value: string) => setCompanyStreetAddress(value)}
                  />
                  <EntryFormInput
                    type='text'
                    placeholder='建物名・部屋番号'
                    value={companyBuilding}
                    onChange={(value: string) => setCompanyBuilding(value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label required">代表者名</label>
                <div className="grid grid-cols-2 gap-4">
                  <EntryFormInput
                    type='text'
                    placeholder='姓'
                    value={representativeLastName}
                    errorMessage={representativeLastNameErr}
                    onChange={(value: string) => {
                      setRepresentativeLastName(value);
                      setRepresentativeLastNameErr(handleRequiredValidation(value));
                    }}
                  />
                  <EntryFormInput
                    type='text'
                    placeholder='名'
                    value={representativeFirstName}
                    errorMessage={representativeFirstNameErr}
                    onChange={(value: string) => {
                      setRepresentativeFirstName(value);
                      setRepresentativeFirstNameErr(handleRequiredValidation(value));
                    }}
                  />
                </div>
              </div>
            </form>
          </section>
          <section>
            <h2 className="text-2xl font-bold mb-8">申請者情報入力</h2>
            <form id="customerInfoForm" className="space-y-6">
              <div className="form-group">

              <label className="form-label required">申請者の役割</label>
              <div className="space-y-4 mb-4">
                {/* Representative Option */}
                <div className="border rounded-md shadow-sm bg-white border-solid border-[#e2e8f0] p-5 mb-4 cursor-pointer" data-role="representative">
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="applicantRole"
                      id="roleRepresentative"
                      className="w-5 h-5 accent-blue-500 cursor-pointer"
                      value="representative"
                      checked={selectedRole === "representative"}
                      onChange={() => setSelectedRole("representative")}
                      required
                    />
                    <div className="flex flex-col cursor-pointer">
                      <label
                        htmlFor="roleRepresentative"
                        className="text-lg font-medium text-gray-800 cursor-pointer"
                      >
                        私は代表者です
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        会社の代表者として申請を行います
                      </p>
                    </div>
                  </div>
                </div>

                {/* Attorney Option */}
                <div className="border rounded-md shadow-sm bg-white border-solid border-[#e2e8f0] p-5 mb-4 cursor-pointer" data-role="attorney">
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="applicantRole"
                      id="roleAttorney"
                      className="w-5 h-5 accent-blue-500 curor-pointer"
                      value="attorney"
                      checked={selectedRole === "attorney"}
                      onChange={() => setSelectedRole("attorney")}
                      required
                    />
                    <div className="flex flex-col cursor-pointer">
                      <label
                        htmlFor="roleAttorney"
                        className="text-lg font-medium text-gray-800 cursor-pointer"
                      >
                        私は担当者で、委任状を持っています
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        代表者からの委任を受けた担当者として申請を行います
                      </p>
                    </div>
                  </div>
                </div>
                {selectedRoleErr && <div className="form-error text-red-500 text-sm mt-1">{selectedRoleErr}</div>}
              </div>

                <label className="form-label required">氏名</label>
                <div className="grid grid-cols-2 gap-4">
                  <EntryFormInput
                    type='text'
                    placeholder='姓'
                    value={lastName}
                    errorMessage={lastNameErr}
                    onChange={(value: string) => {
                      setLastName(value);
                      setLastNameErr(handleRequiredValidation(value));
                    }}
                  />
                  <EntryFormInput
                    type='text'
                    placeholder='名'
                    value={firstName}
                    errorMessage={firstNameErr}
                    onChange={(value: string) => {
                      setFirstName(value);
                      setFirstNameErr(handleRequiredValidation(value));
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <EntryFormInput
                    type='text'
                    placeholder='セイ'
                    value={lastNameKa}
                    errorMessage={lastNameKaErr}
                    onChange={(value: string) => {
                      setLastNameKa(value);
                      const requiredValidation = handleRequiredValidation(value);

                      if(requiredValidation)
                        setLastNameKaErr(requiredValidation);
                      else
                        setLastNameKaErr(handleRegexValidation(
                          value,
                          /^[ァ-ヶー　]+$/,
                          'カタカナで入力してください'
                        ));
                    }}
                  />
                  <EntryFormInput
                    type='text'
                    placeholder='メイ'
                    value={firstNameKa}
                    errorMessage={firstNameKaErr}
                    onChange={(value: string) => {
                      setFirstNameKa(value);
                      const requiredValidation = handleRequiredValidation(value);

                      if(requiredValidation)
                        setFirstNameKaErr(requiredValidation);
                      else
                        setFirstNameKaErr(handleRegexValidation(
                          value,
                          /^[ァ-ヶー　]+$/,
                          'カタカナで入力してください'
                        ));
                    }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label required">生年月日</label>
                  <EntryFormInput
                    type='date'
                    placeholder=''
                    value={birthday}
                    errorMessage={birthdayErr}
                    max="9999-12-31"
                    onChange={(value: string) => {
                      setBirthday(value);
                      setBirthdayErr(handleRequiredValidation(value));
                    }}
                  />
              </div>

              <div className="form-group">
                <label className="form-label required">住所</label>
                <div className="space-y-3">
                  <EntryFormInput
                    type='text'
                    placeholder='郵便番号'
                    value={postalCode}
                    errorMessage={postalCodeErr}
                    onChange={(value: string) => {
                      const numericValue = value.replace(/\D/g, '');

                      let formattedValue = numericValue;

                      if (numericValue.length > 3) {
                        formattedValue = numericValue.slice(0, 3) + '-' + numericValue.slice(3, 7);
                      }

                      setPostalCode(formattedValue);
                      const requiredValidation = handleRequiredValidation(formattedValue);

                      if (requiredValidation) {
                        setPostalCodeErr(requiredValidation);
                      } else {
                        setPostalCodeErr(
                          handleRegexValidation(
                            numericValue,
                            /^\d{7}$/,
                            '正しい郵便番号の形式で入力してください'
                          )
                        );
                      }

                      if(numericValue.length === 7) {
                        handlePostalCodeValidation(numericValue, 'postalCode');
                      }
                    }}
                  />
                  {isLoadingPostalCodeValid && <div className="loading">検索中...</div>}
                  <SelectPrefecture
                    value={prefecture ?? ''}
                    onChange={(value: string) => {
                      setPrefecture(value);
                      setPrefectureErr(handleRequiredValidation(value));
                    }}
                    errorMessage={prefectureErr}
                  />
                  <EntryFormInput
                    type='text'
                    placeholder='市区町村'
                    value={city}
                    onChange={(value: string) => {
                      setCity(value);
                    }}
                  />
                  <EntryFormInput
                    type='text'
                    placeholder='番地'
                    value={address}
                    onChange={(value: string) => {
                      setAddress(value);
                    }}
                  />
                  <EntryFormInput
                    type='text'
                    placeholder='建物名・部屋番号'
                    value={building}
                    onChange={(value: string) => {
                      setBuilding(value);
                    }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label required">性別</label>
                <div className="space-y-2">
                  <div className="flex flex-row">
                    <input
                      type="radio"
                      name="gender"
                      id="male"
                      value="male"
                      onChange={(e) => setGender(e.target.value)}
                      checked={gender === 'male'}
                    />
                    <label htmlFor="male" className="!mb-0">男性</label>
                  </div>
                  <div className="flex flex-row">
                    <input
                      type="radio"
                      name="gender"
                      id="female"
                      value="female"
                      onChange={(e) => setGender(e.target.value)}
                      checked={gender === 'female'}
                    />
                    <label htmlFor="female" className="!mb-0">女性</label>
                  </div>
                </div>
                {genderErr && <div className="form-error text-red-500 text-sm mt-1">{genderErr}</div>}
              </div>
              <div className="form-group">
                <label className="form-label required">電話番号</label>
                <EntryFormInput
                  type='text'
                  placeholder='例：0312345678'
                  value={phoneNum}
                  errorMessage={phoneNumErr}
                  onChange={(value: string) => {
                    setPhoneNum(value);
                    const requiredValidation = handleRequiredValidation(value);
                    
                    if(requiredValidation)
                      setPhoneNumErr(requiredValidation);
                    else
                      setPhoneNumErr(handleRegexValidation(
                        value,
                        /^0\d{9,10}$/,
                        '正しい電話番号の形式で入力してください'
                      ));
                  }}
                />
              </div>

              <div className="form-group">
                <label className="form-label required">メールアドレス</label>
                <EntryFormInput
                  type='text'
                  placeholder='例：example@example.com'
                  value={email}
                  errorMessage={emailErr}
                  onChange={(value: string) => {
                    setEmail(value);
                    const requiredValidation = handleRequiredValidation(value);
                    
                    if(requiredValidation)
                      setEmailErr(requiredValidation);
                    else
                      setEmailErr(handleRegexValidation(
                        value,
                        /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        '正しいメールアドレスの形式で入力してください'
                      ));
                  }}
                />
              </div>

              <div className="form-group">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      id="terms"
                      name="terms"
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      aria-required="true"
                      onChange={() => {
                        setIsAgreeTerm(prev => !prev);
                        setIsAgreeTermErr('');
                      }}
                      checked={isAgreeTerm}
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="terms" className="text-sm text-gray-600">
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800 hover:underline bg-transparent border-none cursor-pointer p-0"
                        onClick={() => setIsTermsModalOpen(true)}
                      >
                        利用規約
                      </button>
                      に同意する
                      <span className="text-red-500 text-xs ml-1">必須</span>
                    </label>
                  </div>
                </div>
                {isAgreeTermErr && <div className="form-error text-red-500 text-sm mt-1">{isAgreeTermErr}</div>}
              </div>
              <div className="form-group">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      id="privacy-policy"
                      name="privacy-policy"
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      aria-required="true"
                      onChange={() => {
                        setIsAgreePrivacyPolicy(prev => !prev);
                        setIsAgreePrivacyPolicyErr('');
                      }}
                      checked={isAgreePrivacyPolicy}
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="privacy-policy" className="text-sm text-gray-600">
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800 hover:underline bg-transparent border-none cursor-pointer p-0"
                        onClick={() => setIsPrivacyPolicyModalOpen(true)}
                      >
                        プライバシーポリシー
                      </button>
                      に同意する
                      <span className="text-red-500 text-xs ml-1">必須</span>
                    </label>
                  </div>
                </div>
                {isAgreePrivacyPolicyErr && <div className="form-error text-red-500 text-sm mt-1">{isAgreePrivacyPolicyErr}</div>}
              </div>
              <div className="mt-8">
                <button
                  type="button"
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
                  onClick={() => {
                    handleValidationToNextStep();
                  }}
                  disabled={isLoadingCompanyPostalCodeValid || isLoadingPostalCodeValid}
                >
                  入力内容の確認へ進む
                </button>
              </div>
            </form>
          </section>
        </main>
      </div>
    </>
  );
};

export default LegalPersonalEntryForm;
